import React, { useState } from 'react';
import { Loader2, KeyRound, Eye, EyeOff, ShieldCheck } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { api } from '@/lib/api';
import { toast } from 'sonner';

/**
 * Reusable "Change password" dialog.
 * Works for any authenticated user (admin, rider, customer).
 */
export const ChangePasswordDialog = ({ open, onOpenChange }) => {
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNext, setShowNext] = useState(false);
  const [loading, setLoading] = useState(false);

  const reset = () => {
    setCurrent(''); setNext(''); setConfirm('');
    setShowCurrent(false); setShowNext(false);
  };

  const hasLetter = /[a-zA-Z]/.test(next);
  const hasNumOrSymbol = /[\d\W_]/.test(next);
  const longEnough = next.length >= 8;
  const passwordsMatch = next && next === confirm;
  const canSubmit = current && longEnough && hasLetter && hasNumOrSymbol && passwordsMatch && !loading;

  const submit = async () => {
    if (!canSubmit) return;
    setLoading(true);
    try {
      await api.post('/auth/change-password', {
        current_password: current,
        new_password: next,
      });
      toast.success('Password aggiornata con successo!');
      reset();
      onOpenChange?.(false);
    } catch (e) {
      const msg = e?.response?.data?.detail;
      toast.error(typeof msg === 'string' ? msg : 'Errore aggiornamento password');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (open) => {
    if (!open) reset();
    onOpenChange?.(open);
  };

  return (
    <Dialog open={open} onOpenChange={handleChange}>
      <DialogContent className="max-w-md" data-testid="change-password-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><KeyRound size={18} className="text-[--brand-primary]"/> Cambia password</DialogTitle>
          <DialogDescription>Aggiorna la password del tuo account. Dovrai riaccedere sui dispositivi collegati.</DialogDescription>
        </DialogHeader>

        <div className="space-y-4 mt-2">
          <div>
            <Label>Password attuale</Label>
            <div className="relative">
              <Input
                type={showCurrent ? 'text' : 'password'}
                value={current}
                onChange={e => setCurrent(e.target.value)}
                className="rounded-xl pr-10"
                autoComplete="current-password"
                data-testid="cp-current-password"
              />
              <button type="button" onClick={() => setShowCurrent(s => !s)}
                      className="absolute right-2 top-1/2 -translate-y-1/2 text-[--brand-muted] hover:text-[--brand-fg]"
                      aria-label={showCurrent ? 'Nascondi' : 'Mostra'}>
                {showCurrent ? <EyeOff size={16}/> : <Eye size={16}/>}
              </button>
            </div>
          </div>

          <div>
            <Label>Nuova password</Label>
            <div className="relative">
              <Input
                type={showNext ? 'text' : 'password'}
                value={next}
                onChange={e => setNext(e.target.value)}
                className="rounded-xl pr-10"
                autoComplete="new-password"
                data-testid="cp-new-password"
              />
              <button type="button" onClick={() => setShowNext(s => !s)}
                      className="absolute right-2 top-1/2 -translate-y-1/2 text-[--brand-muted] hover:text-[--brand-fg]">
                {showNext ? <EyeOff size={16}/> : <Eye size={16}/>}
              </button>
            </div>
            <ul className="mt-2 space-y-1 text-xs">
              <li className={longEnough ? 'text-[--brand-success]' : 'text-[--brand-muted]'}>
                • Almeno 8 caratteri
              </li>
              <li className={hasLetter ? 'text-[--brand-success]' : 'text-[--brand-muted]'}>
                • Contiene almeno una lettera
              </li>
              <li className={hasNumOrSymbol ? 'text-[--brand-success]' : 'text-[--brand-muted]'}>
                • Contiene almeno un numero o simbolo
              </li>
            </ul>
          </div>

          <div>
            <Label>Conferma nuova password</Label>
            <Input
              type={showNext ? 'text' : 'password'}
              value={confirm}
              onChange={e => setConfirm(e.target.value)}
              className="rounded-xl"
              autoComplete="new-password"
              data-testid="cp-confirm-password"
            />
            {confirm && !passwordsMatch && (
              <p className="text-xs text-[--brand-danger] mt-1">Le password non corrispondono</p>
            )}
          </div>
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={() => handleChange(false)} disabled={loading}>Annulla</Button>
          <Button onClick={submit} disabled={!canSubmit} className="rounded-xl bg-[--brand-primary] hover:bg-[--brand-primary-hover]" data-testid="cp-submit-button">
            {loading ? <Loader2 size={14} className="animate-spin mr-2"/> : <ShieldCheck size={14} className="mr-2"/>}
            Aggiorna password
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ChangePasswordDialog;
